import * as signalR from '@microsoft/signalr';
import { NETAPI_URL } from '../config/api';

/**
 * Backend `OnAnnouncementChanged` event payload tipi.
 * Server `QuickSaleAnnouncementController` içinde gönderir.
 */
export interface AnnouncementChangedPayload {
  action: 'created' | 'updated' | 'deleted';
  id: number;
  companyId: string;
  targets: { TargetType: string; TargetId: string }[];
  at: string;
}

class AnnouncementSignalRService {
  private connection: signalR.HubConnection | null = null;
  private companyId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /** id -> callback. Map sayesinde aynı event'e birden çok bileşen abone olabilir
   *  ve unmount sırasında temiz unsubscribe olur (HMR-safe). */
  private changedCallbacks: Map<string, (payload: AnnouncementChangedPayload) => void> = new Map();
  private statusChangeCallbacks: ((state: signalR.HubConnectionState) => void)[] = [];

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  async connect(companyId: string): Promise<void> {
    console.log('🔌 [AnnouncementSignalR] connect() ÇAĞRILDI:', {
      companyId,
      currentState: this.connection?.state,
      currentCompanyId: this.companyId,
      hubUrl: `${NETAPI_URL}/hubs/announcements`,
    });

    if (!companyId) {
      console.warn('[AnnouncementSignalR] companyId boş, bağlantı atlandı.');
      return;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[AnnouncementSignalR] 🟢 Zaten bağlı, listener re-register + grup kontrolü yapılıyor.');
      // HMR sonrası listener kaybolmuş olabilir — yeniden register et.
      this.reRegisterListenersIfNeeded();
      // Şirket değiştiyse gruba yeniden katıl
      if (this.companyId !== companyId) {
        try {
          if (this.companyId) await this.connection.invoke('LeaveCompanyGroup', this.companyId);
          await this.connection.invoke('JoinCompanyGroup', companyId);
          this.companyId = companyId;
          console.log(`[AnnouncementSignalR] Grup değişti: Company_${companyId}`);
        } catch (err) {
          console.warn('[AnnouncementSignalR] Grup değişimi başarısız:', err);
        }
      } else {
        // Aynı şirkete tekrar bağlanma istendi → gruba zaten dahiliz, sessizce atla.
        // Yine de garanti olsun diye join'i bir kez daha dene (idempotent).
        try {
          await this.connection.invoke('JoinCompanyGroup', companyId);
        } catch { /* ignore */ }
      }
      return;
    }

    this.companyId = companyId;

    if (this.connection) {
      try { await this.connection.stop(); } catch { /* ignore */ }
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${NETAPI_URL}/hubs/announcements`, {
        withCredentials: false,
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => (ctx.previousRetryCount < 3 ? 3000 : 10000),
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose(() => {
      console.log('[AnnouncementSignalR] Bağlantı kapandı');
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    });

    this.connection.onreconnecting(() => {
      console.log('[AnnouncementSignalR] Yeniden bağlanıyor...');
      this.notifyStatusChange(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(async () => {
      console.log('[AnnouncementSignalR] Yeniden bağlandı');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;
      await this.joinGroup();
    });

    this.registerListeners();

    try {
      await this.connection.start();
      console.log('[AnnouncementSignalR] Bağlantı kuruldu');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      await this.joinGroup();
    } catch (err) {
      console.error('[AnnouncementSignalR] Bağlantı başarısız:', err);
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      if (this.companyId) {
        try { await this.connection.invoke('LeaveCompanyGroup', this.companyId); } catch { /* ignore */ }
      }
      await this.connection.stop();
    } catch (err) {
      console.warn('[AnnouncementSignalR] Disconnect hatası:', err);
    }
    this.connection = null;
    this.companyId = null;
  }

  private async joinGroup(): Promise<void> {
    if (!this.connection || !this.companyId) return;
    try {
      await this.connection.invoke('JoinCompanyGroup', this.companyId);
      console.log(`[AnnouncementSignalR] Grup'a katıldı: Company_${this.companyId}`);
    } catch (err) {
      console.warn('[AnnouncementSignalR] JoinCompanyGroup hatası:', err);
    }
  }

  private registerListeners(): void {
    if (!this.connection) return;

    this.connection.on('OnAnnouncementChanged', (payload: AnnouncementChangedPayload) => {
      console.log('📢 [AnnouncementSignalR] OnAnnouncementChanged:', payload);
      this.changedCallbacks.forEach((cb) => {
        try { cb(payload); } catch (err) { console.warn('[AnnouncementSignalR] callback hatası:', err); }
      });
    });

    this.connection.on('GroupJoined', (info: any) => {
      console.log('[AnnouncementSignalR] GroupJoined:', info);
    });

    this.connection.on('Pong', (info: any) => {
      console.log('[AnnouncementSignalR] Pong:', info);
    });
  }

  /** HMR/hot-reload sonrası listener'lar kaybolabilir — duplicate önlemek için
   *  off-then-on pattern kullanıyoruz. */
  private reRegisterListenersIfNeeded(): void {
    if (!this.connection) return;
    const eventNames = ['OnAnnouncementChanged', 'GroupJoined', 'Pong'];
    eventNames.forEach((name) => this.connection!.off(name));
    this.registerListeners();
  }

  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this.companyId) return;
    this.reconnectAttempts++;
    const delay = Math.min(2000 * this.reconnectAttempts, 10000);
    setTimeout(() => {
      console.log(`[AnnouncementSignalR] Reconnect denemesi ${this.reconnectAttempts}`);
      if (this.companyId) this.connect(this.companyId).catch(() => { /* ignore */ });
    }, delay);
  }

  private notifyStatusChange(state: signalR.HubConnectionState): void {
    this.statusChangeCallbacks.forEach((cb) => {
      try { cb(state); } catch { /* ignore */ }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Public callback API
  // ─────────────────────────────────────────────────────────────

  /** Duyuru oluştur/güncelle/sil event'ine abone ol. */
  onAnnouncementChanged(id: string, callback: (payload: AnnouncementChangedPayload) => void): void {
    this.changedCallbacks.set(id, callback);
  }

  removeAnnouncementChangedCallback(id: string): void {
    this.changedCallbacks.delete(id);
  }

  onStatusChange(callback: (state: signalR.HubConnectionState) => void): void {
    this.statusChangeCallbacks.push(callback);
  }
}

const announcementSignalRService = new AnnouncementSignalRService();
export default announcementSignalRService;
